import { ComponentProps, ReactNode, Ref, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/tailwind';

import { Button } from '../Button/Button';

export type InputProps = {
  label?: string | ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  hintText?: string;
  hintIcon?: ReactNode;
  isError?: boolean;
  as?: 'input' | 'textarea';
  optional?: boolean;
  clearable?: boolean;
  inputClassName?: string;
  onClear?: () => void;
} & ComponentProps<'input'> &
  ComponentProps<'textarea'>;

const Input = ({
  className,
  type,
  label,
  leftIcon,
  rightIcon,
  hintText,
  hintIcon,
  isError,
  disabled,
  readOnly,
  as = 'input',
  optional,
  clearable,
  value,
  inputClassName,
  onClear,
  ...props
}: InputProps) => {
  const { t } = useTranslation('components');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const Element = as;

  const handleClick = () => {
    inputRef.current?.focus();
  };

  return (
    <>
      {label && (
        <button
          className="flex justify-between items-center mb-2 cursor-pointer w-full"
          onClick={() => inputRef.current?.focus()}
        >
          <p className="text-body-14-medium text text-custom-warm-grey-700 min-h-[20px]">
            {label}{' '}
            {optional && (
              <span className="text-sm font-medium text-custom-warm-grey-400">(optional)</span>
            )}
          </p>
          {clearable && value && String(value).length > 0 && !disabled && !readOnly && (
            <Button
              type="button"
              variant="teritary"
              size="xxs"
              onClick={e => {
                e.stopPropagation();
                if (onClear) {
                  onClear();
                }
              }}
              className="p-0 hover:bg-transparent hover:underline h-5"
            >
              {t('button.clear')}
            </Button>
          )}
        </button>
      )}
      <button
        onClick={handleClick}
        type="button"
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleClick();
          }
        }}
        className={cn(
          'flex items-start px-3 py-2 rounded-[6px] outline transition-colors hover:bg-custom-warm-grey-100',
          isError ? '!outline-custom-red-600' : 'outline-custom-warm-grey-200',
          disabled ? '!pointer-events-none bg-custom-warm-grey-100' : 'bg-custom-warm-grey-25',
          readOnly
            ? '!pointer-events-none !bg-custom-warm-grey-150'
            : 'focus-within:outline-custom-cyan-400',
          className
        )}
      >
        {leftIcon && <span className="text-custom-warm-grey-500 mr-2">{leftIcon}</span>}

        <Element
          ref={inputRef as unknown as Ref<any>}
          className={cn(
            'text-body-14-regular focus:outline-0 placeholder:text-custom-warm-grey-400 bg-transparent w-full min-h-[20px]',
            as === 'textarea' && 'min-h-[84px] resize-none',
            readOnly && 'placeholder:text-subtitle-secondary',
            type === 'number' &&
              'appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [moz-appearance:textfield]',
            inputClassName
          )}
          disabled={disabled}
          readOnly={readOnly}
          data-slot="input"
          type={as === 'input' ? type : undefined}
          value={value}
          {...props}
        />

        {rightIcon && (
          <span
            className={cn(
              'ml-3 size-5 flex items-center justify-center',
              isError ? 'text-custom-red-600' : 'text-custom-warm-grey-500'
            )}
          >
            {rightIcon}
          </span>
        )}
      </button>
      {hintText && (
        <div
          className={cn(
            'mt-1.5 flex items-center gap-1.5 text-body-12-regular',
            isError ? '!text-custom-red-600' : '!text-custom-warm-grey-500'
          )}
        >
          {hintIcon}
          <p>{hintText}</p>
        </div>
      )}
    </>
  );
};

export { Input };
